import { useEffect, useRef, useState, type FormEvent } from 'react'
import { router, useForm } from '@inertiajs/react'
import AccountLayout from '@/layouts/AccountLayout'
import PageHeader from '@/components/PageHeader'
import type { Facilitator } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface FacilitatorsProps {
  facilitators: Facilitator[]
}

export default function Facilitators({ facilitators }: FacilitatorsProps) {
  const ap = useAccountPath()
  const newDialog = useRef<HTMLDialogElement>(null)
  const editDialog = useRef<HTMLDialogElement>(null)
  const [editRow, setEditRow] = useState<Facilitator | null>(null)

  function openEdit(f: Facilitator) {
    setEditRow(f)
    editDialog.current?.showModal()
  }

  function handleDelete(id: number) {
    if (!confirm('Delete this facilitator? They will be removed from all surveys.')) return
    router.delete(ap(`/facilitators/${id}`), { preserveScroll: true })
  }

  return (
    <AccountLayout>
      <div className="space-y-6">
        <PageHeader
          title="Facilitators"
          description="Facilitators are the coaches and workshop leaders who deliver your programmes. Assign one to a survey to get a share link tagged with their name — responses collected through it can be filtered by facilitator in Results. Facilitators don't get a login or see results; their link is a normal survey link that records who ran the session."
          actions={
            <button onClick={() => newDialog.current?.showModal()} className="btn btn-primary btn-sm">
              <span className="iconify lucide--plus size-4" />
              New Facilitator
            </button>
          }
        />

        <div className="card bg-base-100">
          {facilitators.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table text-sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th className="text-end">Surveys</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {facilitators.map((f) => (
                    <tr key={f.id} className="hover:bg-base-200/40">
                      <td className="font-medium">{f.name}</td>
                      <td className="text-base-content/70">{f.email_address}</td>
                      <td className="text-end tabular-nums">{f.survey_count}</td>
                      <td>
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEdit(f)} className="btn btn-square btn-ghost btn-sm" title="Edit">
                            <span className="iconify lucide--pencil size-4" />
                          </button>
                          <button onClick={() => handleDelete(f.id)} className="btn btn-square btn-ghost btn-sm text-error" title="Delete">
                            <span className="iconify lucide--trash-2 size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card-body items-center text-center py-12">
              <span className="iconify lucide--users size-12 text-base-content/20 mb-3" />
              <h2 className="text-lg font-semibold">No facilitators yet</h2>
              <p className="text-base-content/50 text-sm max-w-sm">
                Add facilitators to generate unique survey links for each coach or workshop leader.
              </p>
            </div>
          )}
        </div>
      </div>

      <NewFacilitatorModal dialogRef={newDialog} createPath={ap('/facilitators')} />
      <EditFacilitatorModal
        dialogRef={editDialog}
        row={editRow}
        basePath={ap('/facilitators')}
        onClosed={() => setEditRow(null)}
      />
    </AccountLayout>
  )
}

function NewFacilitatorModal({ dialogRef, createPath }: { dialogRef: React.RefObject<HTMLDialogElement | null>; createPath: string }) {
  const { data, setData, post, processing, reset } = useForm({ first_name: '', last_name: '', email_address: '' })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post(createPath, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        dialogRef.current?.close()
      },
    })
  }

  return (
    <dialog ref={dialogRef} className="modal">
      <div className="modal-box max-w-md">
        <h3 className="text-lg font-bold">New facilitator</h3>
        <p className="text-sm text-base-content/60">Once created, assign them to a survey from the survey's Overview page to get their tagged link.</p>
        <form onSubmit={handleSubmit} className="py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <fieldset className="fieldset">
              <legend className="fieldset-legend">First name</legend>
              <input type="text" className="input input-sm w-full" value={data.first_name} onChange={(e) => setData('first_name', e.target.value)} required />
            </fieldset>
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Last name</legend>
              <input type="text" className="input input-sm w-full" value={data.last_name} onChange={(e) => setData('last_name', e.target.value)} />
            </fieldset>
          </div>
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Email</legend>
            <input type="email" className="input input-sm w-full" value={data.email_address} onChange={(e) => setData('email_address', e.target.value)} required />
          </fieldset>
          <div className="modal-action">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => dialogRef.current?.close()}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={processing || !data.first_name.trim() || !data.email_address.trim()}>
              {processing && <span className="loading loading-spinner loading-sm" />}
              Create
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop"><button>close</button></form>
    </dialog>
  )
}

function EditFacilitatorModal({ dialogRef, row, basePath, onClosed }: { dialogRef: React.RefObject<HTMLDialogElement | null>; row: Facilitator | null; basePath: string; onClosed: () => void }) {
  const { data, setData, patch, processing } = useForm({ first_name: '', last_name: '', email_address: '' })
  // The modal stays mounted; sync the form whenever a row is opened. The parent
  // clears its editRow to null on close, so reopening the same row is a real
  // dependency change and always re-syncs (no stale values).
  useEffect(() => {
    if (row) setData({ first_name: row.first_name, last_name: row.last_name, email_address: row.email_address })
  }, [row]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!row) return
    patch(`${basePath}/${row.id}`, {
      preserveScroll: true,
      onSuccess: () => dialogRef.current?.close(),
    })
  }

  return (
    <dialog ref={dialogRef} className="modal" onClose={onClosed}>
      <div className="modal-box max-w-md">
        <h3 className="text-lg font-bold">Edit facilitator</h3>
        <p className="text-sm text-base-content/60">Changes apply everywhere this facilitator appears, including existing survey links.</p>
        <form onSubmit={handleSubmit} className="py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <fieldset className="fieldset">
              <legend className="fieldset-legend">First name</legend>
              <input type="text" className="input input-sm w-full" value={data.first_name} onChange={(e) => setData('first_name', e.target.value)} required />
            </fieldset>
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Last name</legend>
              <input type="text" className="input input-sm w-full" value={data.last_name} onChange={(e) => setData('last_name', e.target.value)} />
            </fieldset>
          </div>
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Email</legend>
            <input type="email" className="input input-sm w-full" value={data.email_address} onChange={(e) => setData('email_address', e.target.value)} required />
          </fieldset>
          <div className="modal-action">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => dialogRef.current?.close()}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={processing}>
              {processing && <span className="loading loading-spinner loading-sm" />}
              Save
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop"><button>close</button></form>
    </dialog>
  )
}
